<template>
  <div
    v-on-clickaway="close"
    :class="classes"
  >
    <div
      class="date-picker__field-wrapper"
      @click="onToggle"
    >
      <label v-if="label" class="date-picker__label">
        {{ label }}
        <span v-if="required" class="date-picker__label-required">*</span>
      </label>

      <div class="date-picker__field">
        <div class="date-picker__text">
          <span class="date-picker__date">{{ formatDate(date) ? formatDate(date) : placeholder }}</span>
        </div>
        <span class="date-picker__icon-calendar icon icon-calendar"/>
      </div>
    </div>

    <transition name="slide-from-top-short">
      <div
        v-show="isOpened"
        class="date-picker__dropdown"
      >
        <div
          key="date-picker"
          class="datepicker__dropdown-date"
        >
          <!--          maximum-view="day"-->
          <datepicker
            ref="calendar"
            v-model="selectedDate"
            :language="datapickerLang"
            :disabledDates="disabledDates"
            :open-date="openDate"
            monday-first
            full-month-name
            inline
            format="dd/MM/yy"
            calendar-class="date-picker__calendar"
            wrapper-class="date-picker__wrap"
            @input="onSelect"
            @changedMonth="changedMonth"
          />
        </div>
      </div>
    </transition>

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="datepicker__error-msg"
      >
        {{ errorMsg }}
        <ErrorIcon/>
      </span>
    </transition>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { uk, ro, ru, enUS } from 'date-fns/locale'
  import { format } from 'date-fns'
  import datepicker from 'vuejs-datepicker'
  import {
    uk as datapickerUk,
    ro as datapickerRo,
    ru as datapickerRu,
    en as datapickerEn
  } from 'vuejs-datepicker/dist/locale'
  import { mixin as clickaway } from 'vue-clickaway'
  import crud from './crud'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'


  export default {
    name: 'DatePicker',
    components: {
      datepicker,
      ErrorIcon
    },
    mixins: [clickaway, crud],
    props: {
      required: {
        type: Boolean,
        default: false
      },
      items: {
        type: Array,
        default: () => ([])
      },
      name: {
        type: String,
        default: ''
      },
      placeholder: String,
      reset: Boolean,
      timestamp: {
        type: Number
      },
      errorMsg: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      label: {
        type: String
      }
    },

    data () {
      return {
        disabledDates: {},
        openDate: '',
        isOpened: false,
        selectedDate: null,
        selectedLabel: null,
        isCollapsed: true,
        date: null,
        hours: 0,
        minutes: 0,
        isDropdownVisible: false,
        error: null,
        datapickerLang: datapickerUk,
        timepickerLang: uk
      }
    },

    computed: {
      ...mapState({
        serverTimestamp: (state) => state.globalData && state.globalData.currentTime,
        showroomPeriodEnabled: state => state.consultation.showroomPeriodEnabled,
        showroomPeriodDisabled: state => state.consultation.showroomPeriodDisabled,
        currentLang: state => state.currentLang,
        currentCountry: state => state.currentCountry
      }),
      classes () {
        return [
          'date-picker',
          {
            'date-picker_active': this.isDropdownVisible,
            'date-picker_error': this.error
          }
        ]
      },

      selected () {
        return this.selectedLabel
      }
    },

    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      },

      serverTimestamp () {
        this.setCalendarOptions()
      }
    },

    created () {
      if (this.currentLang === 'ro') {
        this.datapickerLang = datapickerRo
        this.timepickerLang = ro
      } else if (this.currentLang === 'ru') {
        this.datapickerLang = datapickerRu
        this.timepickerLang = ru
      } else if (this.currentLang === 'uk') {
        this.datapickerLang = datapickerUk
        this.timepickerLang = uk
      } else if (this.currentLang === 'en') {
        this.datapickerLang = datapickerEn
        this.timepickerLang = enUS
      } else {
        if (this.currentCountry === 'md') {
          this.datapickerLang = datapickerRo
          this.timepickerLang = ro
        } else if (this.currentCountry === 'ru') {
          this.datapickerLang = datapickerRu
          this.timepickerLang = ru
        }
      }

      this.setCalendarOptions()
    },

    mounted () {
      if (this.value) {
        this.onSelect(new Date(Math.floor(this.value * 1000.0)))
      }

      this.customDatePicker()
    },

    methods: {
      setCalendarOptions () {
        if (!this.serverTimestamp || !this.showroomPeriodEnabled) return
        let dataUTC = new Date(this.serverTimestamp * 1000).setHours(0, 0, 0, 0) // not includes
        dataUTC = new Date(dataUTC)
        this.openDate = dataUTC
        this.$set(this.disabledDates, 'to', dataUTC)
        this.$set(this.disabledDates, 'days', this.disabledWeekDays())
        this.$set(this.disabledDates, 'dates', this.disabledDay())
      },

      changedMonth (e) {
        let date
        if (e instanceof Date) {
          date = new Date(e)
        } else if (e && e.timestamp) {
          date = new Date(e.timestamp)
        }

        const month = date.getMonth()
        const year = date.getFullYear()
        this.customDatePicker(this.datapickerLang._months[month], year)
      },
      customDatePicker (month, year) {
        const dayMonth = this.$el.querySelector('.day__month_btn.up')
        if (!dayMonth) return

        if (month || year) {
          dayMonth.innerHTML = this.renderDatePicker(month, year)
        } else {
          const [monthText, yearText] = dayMonth.innerHTML.split(' ')
          dayMonth.innerHTML = this.renderDatePicker(monthText, yearText)
        }
      },
      renderDatePicker (month, year) {
        this.month = month || this.month
        this.year = year || this.year
        return `
            ${this.month} <i class="custom-year">${this.year}</i>
          `
      },
      disabledDay () {
        const MINUTES_INTERVAL_TIME = 119
        const serverDateUTC = new Date(this.serverTimestamp * 1000)
        const serverDateTimestamp = new Date(this.serverTimestamp * 1000).getTime()
        let serverDay = serverDateUTC.getDay() - 1
        if (serverDay === -1) serverDay = 6

        const timiList = this.showroomPeriodEnabled && this.showroomPeriodEnabled[serverDay]
        if (!timiList) return []

        const isTime = timiList.every(item => {
          const hours = Number(item.value.split(':')[0])
          const minutes = Number(item.value.split(':')[1])
          let itemTimestamp = new Date(this.serverTimestamp * 1000)
          itemTimestamp.setHours(hours, 0, 0, 0)
          itemTimestamp.setMinutes(minutes)
          itemTimestamp = itemTimestamp.getTime()
          return !(this.timeDifferenceMinute(itemTimestamp, serverDateTimestamp) >= MINUTES_INTERVAL_TIME)
        })

        if (isTime) {
          return [new Date(this.serverTimestamp * 1000)]
        }
      },

      disabledWeekDays () {
        return this.showroomPeriodDisabled.map(dayIndex => {
          return dayIndex === 6
            ? 0
            : dayIndex + 1
        })
      },

      onToggle () {
        this.error = false
        this.isOpened = !this.isOpened
      },

      formatDate (date) {
        if (date) {
          return format(date, 'd MMMM yyyy', { locale: this.timepickerLang }).replace(/ /g, ' ')
        }
      },

      onSelect (date) {
        this.date = date
        this.selectedLabel = date.toLocaleString('uk', {
          day: 'numeric',
          month: 'numeric',
          year: '2-digit'
        }).replace(/\./g, '-')

        this.selectedDate = date.getTime()
        this.$emit('input', this.selectedLabel)
        this.$emit('timestampInput', Math.floor(date.getTime() / 1000.0))
        this.close()
      },

      close () {
        this.isOpened = false
      },

      open () {
        this.isOpened = true
      }
    }
  }
</script>

<style lang="scss">
$padding-for-error: 28px;

.date-picker {
  position: relative;
  display: block;
  align-self: flex-start;
  min-width: 290px;
  // max-width: 335px;
  padding-bottom: $padding-for-error;
  cursor: pointer;
  margin-top: 0;

  @include mobile {
    max-width: 100%;
  }

  &.disabled {
    pointer-events: none;

    .datepicker__field {
      background: $c-gray-4;
    }

    .datepicker__text,
    .datepicker__date {
      color: $c-gray-7;
    }
  }

  &__label {
    user-select: none;
    position: absolute;
    z-index: 1;
    top: 8px;
    left: 16px;
    font-size: 13px;
    transform: translateY(0);
    display: inline-block;
    //padding-bottom: 1em;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    color: $c-gray-7;
    //transition: 0.35s;

    &-required {
      color: $c-errorDark;
    }
  }

  &__field {
    position: relative;
    display: block;
    width: 100%;
    min-height: 58px;
    padding: 17px 19px 17px 16px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 1;
    background-color: $c-white;
    border: 1px solid $c-gray-4;
    outline: none;
    caret-color: $c-active-light;
    transition: 0.3s;
    appearance: none !important;
    -webkit-appearance: none !important;
    border-radius: 0 !important;

    &:focus {
      background-color: $c-gray-4;
    }

    @include hover {
      background-color: $c-gray-1;

      + .datepicker__label {
        color: $c-gray-dark;
      }
    }
  }

  &__icon-calendar {
    position: absolute;
    top: 50%;
    right: 6px;
    padding: 0;
    font-size: 26px;
    transition: 0.3s;
    transform: translateY(-50%);

    &::before {
      color: #555;
    }
  }

  &__date {
    margin-right: 50px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    color: $c-gray-dark;
  }

  &__text {
    position: relative;
    top: 6px;
    padding-right: 20px;
    color: $c-gray-dark;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dropdown {
    max-width: 338px;
    padding: 5px 5px 15px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    cursor: default;
    user-select: none;
    border-radius: 4px;
    position: absolute;
    top: calc(100% - #{$padding-for-error});
    left: 0;
    z-index: 999;
    width: 100%;
    background: $c-gray-1;
    transition: 0.3s;
    -webkit-overflow-scrolling: touch;
    //border-bottom: 1px solid $c-gray-4;

    @include mobile {
      top: calc(100% - 20px);
      right: 0;
      padding: 5px 0;
    }

    .vdp-datepicker__calendar {
      width: 100%;
      padding-left: 4px;
      margin: 0 auto;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      background: transparent;
      border: none;

      @include mobile {
        padding-left: 27px;
        transform: translateX(-13px);
      }

      @include mobileXs {
        padding-left: 0;
        transform: translateX(1px);
      }

      header {
        display: flex;
        justify-content: space-between;
        padding: 0 15%;

        .day__month_btn.up {
          transition: color 0.3s;

          &:hover {
            color: $c-active;
            background: transparent;
          }
        }
      }

      .prev,
      .next {
        overflow: hidden;
        background: url('../../assets/img/icons/chevron.svg') no-repeat center;
        background-size: 15px;
        opacity: 0.7;
        transition: background 0.3s, opacity 0.3s;
        transform: rotate(-180deg);

        &::after {
          display: none;
        }

        &:hover:not(.disabled) {
          background: url('../../assets/img/icons/chevron-red.svg') no-repeat center;
          background-size: 15px;
          opacity: 1;
        }

        &.disabled {
          opacity: 0.2;
        }
      }

      .prev {
        transform: rotate(0deg);
      }

      .cell {
        border: none !important;
        transition: all 0.3s;

        &.month {
          &:hover {
            color: $c-active-light;
            background: transparent;
          }
        }

        &.selected {
          color: $c-white !important;
          background: $c-active-light;
        }

        &.weekend {
          &:hover {
            color: $c-white;
          }
        }

        &:hover {
          color: $c-white !important;
          background: $c-black !important;
        }

        &.day {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: unset;
          aspect-ratio: 1;
          line-height: normal;
          //width: 32px;
          //height: 32px;
          padding: 0;
          //margin: 1px 6.2px 0;
          //line-height: 32px;
          border-radius: 50%;

          @include mobile {
            width: 14.28%;
            margin: 0;
          }
        }

        &.day-header,
        &.blank {
          color: $c-gray-dark;

          &:hover {
            color: $c-gray-dark;
            cursor: default;
            background: transparent;
          }
        }

        &.disabled {
          color: $c-gray-medium;
          pointer-events: none;
        }
      }
    }

    &-button {
      border-radius: 0;

      &-container {
        display: flex;
        justify-content: center;
        margin: 20px 0;
      }

      background-color: $c-white;
      transition: all 0.4s;

      &:hover {
        background-color: $c-black !important;
        color: $c-white !important;
        // box-shadow: 0 2px 10px 0 $c-gray-medium;
      }
    }

    &-time {
      padding-right: 10px;
      padding-left: 10px;

      &-title {
        margin: 10px 0 25px;
        text-align: center;
      }
    }

    &-times {
      display: flex;
      flex-wrap: wrap;
      margin-top: 10px;
    }

    &-time-item {
      flex: 0 1 20%;
      margin-bottom: 25px;
      color: $c-gray-dark;
      text-align: center;
      cursor: pointer;
      transition: color 0.3s;

      &:hover {
        color: $c-active;
      }

      &_disabled {
        color: $c-gray-medium;
        pointer-events: none;
      }

      &_selected {
        color: $c-active;
      }
    }

    &-back-to-calendar-button {
      display: flex;
      align-items: center;
      margin-right: 5px;
      cursor: pointer;

      &-arrow {
        width: 16px;
        height: 16px;
        margin-right: 10px;
        transition: 0.3s;
      }

      &-text {
        font-size: 12px;
        line-height: 16px;
        transition: color 0.3s;
      }

      &:hover {
        .datepicker__dropdown-back-to-calendar-button {
          &-text {
            color: $c-active;
          }

          &-arrow {
            stroke: $c-active;
          }
        }
      }
    }
  }

  &__error-msg {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    left: 0;
    font-size: 12px;
    color: $c-errorDark;
    height: 20px;
    bottom: 4px;
    width: 100%;

    img,
    svg {
      display: flex;
      min-width: 14px;
      min-height: 15px;
      width: 14px;
      height: 15px;
      object-fit: contain;
    }
  }

  &_error {
    .date-picker__field {
      border: 1px solid $c-errorDark;
    }
  }

  &_active {
    .datepicker {
      &__field {
        background-color: $c-gray-1 !important;
      }

      &__arrow {
        transform: rotate(0deg) translateY(-50%) !important;
      }

      //&__label {
      //  color: $c-gray-dark;
      //}
    }
  }
}
</style>
